import { useSceneEvents } from "@/components/common/scene-events-context";
import {
  formatArea,
  formatDistance,
} from "@/components/common/settings-context/distance-unit-utils";
import { useDistanceUnit } from "@/components/common/settings-context/settings-context";
import { AppAwareHtml } from "@/components/r3f/renderers/app-aware-html";
import { PointCloudObject } from "@/object-cache";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import { selectActiveTool } from "@/store/ui/ui-selectors";
import { deactivateTool, ToolName } from "@/store/ui/ui-slice";
import {
  getPickedPoint,
  grey,
  NoTranslate,
} from "@faro-lotv/app-component-toolbox";
import { FaroIconButton, useToast } from "@faro-lotv/flat-ui";
import FilterCenterFocusIcon from "@mui/icons-material/FilterCenterFocus";
import AdsClickIcon from "@mui/icons-material/AdsClick";
import CloseIcon from "@mui/icons-material/Close";
import { Box, LinearProgress, Paper, Tooltip, Typography } from "@mui/material";
import { Line } from "@react-three/drei";
import { useThree } from "@react-three/fiber";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  type JSX,
} from "react";
import {
  Camera,
  Color,
  DoubleSide,
  Matrix4,
  Object3D,
  Plane,
  Vector3,
} from "three";
import { useStore } from "zustand";
import { ToolControlsRef } from "../tool-controls-interface";
import { surfaceDimensions } from "./depth-surface";
import {
  CYLINDER_DEVIATION_COLORS,
  CYLINDER_RING_STYLE,
} from "./cylinder-surface";
import { useSurfaceReport } from "./use-surface-report";
import {
  positionSurfaceCallout,
  SURFACE_CALLOUT_MARGIN,
} from "./surface-callout-position";
import {
  isAnalyticSelection,
  isPipeRunSelection,
  wandSelectionShape,
  type WandSelection,
} from "./wand-selection";
import { PIPE_RUN_COLOR } from "./pipe-selection-pass";
import { cylinderDeviationHistogram } from "./cylinder-deviation-histogram";
import type { SurfaceSelection } from "./surface-selection-pass";
import StopIcon from "@mui/icons-material/Stop";
import {
  clearSurfaceWand,
  closeSurfaceWand,
  removeSurfaceWandSelection,
  activateSurfaceWandSelection,
  connectSurfaceWandToolbar,
  requestSurfaceWand,
  useSurfaceWandStore,
} from "./surface-wand-context";

const CALLOUT_WIDTH = 216;
const CALLOUT_HEIGHT = 160;

/** @returns Picking controls and the bounded selected plane. */
export const SurfaceWandTool = forwardRef<
  ToolControlsRef,
  { activePointCloud: PointCloudObject }
>(function SurfaceWandTool({ activePointCloud }, ref): JSX.Element | null {
  const store = useSurfaceWandStore();
  if (!store) return null;
  return (
    <WandControls store={store} activePointCloud={activePointCloud} ref={ref} />
  );
});

const WandControls = forwardRef<
  ToolControlsRef,
  {
    store: NonNullable<ReturnType<typeof useSurfaceWandStore>>;
    activePointCloud: PointCloudObject;
  }
>(function WandControls({ store, activePointCloud }, ref): JSX.Element | null {
  const camera = useThree((state) => state.camera);
  const active = useAppSelector(selectActiveTool) === ToolName.surfaceWand;
  useSurfaceReport(activePointCloud, store, active);
  const dispatch = useAppDispatch();
  const selection = useStore(store, (state) => state.selection);
  const retained = useStore(store, (state) => state.retainedSelections);
  const isolated = useStore(store, (state) => state.isolateSelection);
  const computing = useStore(store, (state) => !!state.request);
  const error = useStore(store, (state) => state.error);
  const { openToast } = useToast();
  const events = useSceneEvents();
  useEffect(() => {
    if (!active) return;
    return connectSurfaceWandToolbar(store, events);
  }, [active, store, events]);
  useEffect(() => {
    if (selection?.coverage === "partial") {
      openToast({
        title: "Surface partially selected",
        message:
          "Expansion stopped before all boundaries could be checked. The current selection has been kept.",
        variant: "warning",
      });
    }
  }, [selection?.coverage, openToast]);
  useEffect(() => {
    if (error) {
      openToast({
        title: "Surface not selected",
        message: error,
        variant: "warning",
      });
    }
  }, [error, openToast]);
  useEffect(() => {
    if (!active) closeSurfaceWand(store);
  }, [active, store]);
  useEffect(() => () => clearSurfaceWand(store), [activePointCloud, store]);
  useEffect(() => {
    if (!active) return;
    function keyDown(event: KeyboardEvent): void {
      if (
        event.target instanceof HTMLElement &&
        (event.target.isContentEditable ||
          ["INPUT", "TEXTAREA"].includes(event.target.tagName))
      ) {
        return;
      }
      if (event.key === "Escape") dispatch(deactivateTool());
      if (event.key === "Delete" || event.key === "Backspace") {
        clearSurfaceWand(store);
      }
    }
    window.addEventListener("keydown", keyDown);
    return () => window.removeEventListener("keydown", keyDown);
  }, [active, dispatch, store]);
  useImperativeHandle(ref, () => ({
    pointClicked(event, cloudId) {
      if (
        !active ||
        cloudId !== activePointCloud.iElement.id ||
        event.camera !== camera
      ) {
        return;
      }
      event.stopPropagation();
      const point = getPickedPoint(event);
      camera.updateMatrixWorld();
      requestSurfaceWand(store, {
        cloudId,
        cameraId: camera.uuid,
        point,
        inverseViewProjection: new Matrix4().multiplyMatrices(
          camera.matrixWorld,
          camera.projectionMatrixInverse,
        ),
      });
      events.invalidatePointCloudScene.emit();
    },
  }));
  return (
    <>
      {!isolated &&
        retained
          .filter(
            (surface) =>
              surface.cloudId === activePointCloud.iElement.id &&
              surface.cameraId === camera.uuid,
          )
          .map((surface) => (
            <SurfacePlaneOverlay
              key={surface.id}
              selection={surface}
              up={camera.up}
              computing={false}
              isolated={false}
              clippingPlanes={activePointCloud.material.clippingPlanes}
              onActivate={() => activateSurfaceWandSelection(store, surface)}
              onRemove={() => removeSurfaceWandSelection(store, surface)}
              onToggleIsolation={() => {
                activateSurfaceWandSelection(store, surface);
                store.setState({ isolateSelection: true });
              }}
            />
          ))}
      {selection?.cloudId === activePointCloud.iElement.id &&
        selection.cameraId === camera.uuid && (
          <SurfacePlaneOverlay
            selection={selection}
            up={camera.up}
            computing={computing}
            isolated={isolated}
            clippingPlanes={activePointCloud.material.clippingPlanes}
            onToggleIsolation={() =>
              store.setState({ isolateSelection: !isolated })
            }
            onRemove={() => removeSurfaceWandSelection(store, selection)}
          />
        )}
    </>
  );
});

function SurfacePlaneOverlay({
  selection,
  up,
  computing,
  isolated,
  onToggleIsolation,
  onActivate,
  onRemove,
  clippingPlanes,
}: {
  selection: WandSelection;
  up: Vector3;
  computing: boolean;
  isolated: boolean;
  onToggleIsolation(): void;
  onActivate?(): void;
  onRemove(): void;
  clippingPlanes: Plane[] | null;
}): JSX.Element {
  if (isPipeRunSelection(selection)) {
    const observed: Vector3[] = [];
    const inferred: Vector3[] = [];
    for (let offset = 0; offset < selection.centerline.length; offset += 3) {
      observed.push(new Vector3().fromArray(selection.centerline, offset));
    }
    for (
      let offset = 0;
      offset < selection.inferredCenterline.length;
      offset += 3
    ) {
      inferred.push(
        new Vector3().fromArray(selection.inferredCenterline, offset),
      );
    }
    return (
      <group position={selection.origin}>
        <SurfaceDimensionsCallout
          {...{
            selection,
            up,
            computing,
            isolated,
            onToggleIsolation,
            onActivate,
            onRemove,
          }}
        />
        {observed.length > 1 && (
          <Line
            points={observed}
            segments
            color={PIPE_RUN_COLOR}
            lineWidth={2}
            depthWrite={false}
            depthTest={false}
            toneMapped={false}
            raycast={() => undefined}
            clippingPlanes={clippingPlanes}
          />
        )}
        {inferred.length > 1 && (
          <Line
            points={inferred}
            segments
            color={PIPE_RUN_COLOR}
            lineWidth={2}
            dashed
            dashSize={0.05}
            gapSize={0.025}
            depthWrite={false}
            depthTest={false}
            toneMapped={false}
            raycast={() => undefined}
            clippingPlanes={clippingPlanes}
          />
        )}
      </group>
    );
  }
  const outlinePoints: Vector3[] = [];
  const axisPoints: Vector3[] = [];
  const ringPoints: Vector3[] = [];
  if (selection.rings) {
    for (let offset = 0; offset < selection.rings.length; offset += 3) {
      ringPoints.push(new Vector3().fromArray(selection.rings, offset));
    }
  }
  if (selection.centerline) {
    for (let offset = 0; offset < selection.centerline.length; offset += 3) {
      axisPoints.push(new Vector3().fromArray(selection.centerline, offset));
    }
  }
  for (let offset = 0; offset < selection.outline.length; offset += 3) {
    outlinePoints.push(new Vector3().fromArray(selection.outline, offset));
  }
  return (
    <group position={selection.origin}>
      <SurfaceDimensionsCallout
        selection={selection}
        up={up}
        computing={computing}
        isolated={isolated}
        onToggleIsolation={onToggleIsolation}
        onActivate={onActivate}
        onRemove={onRemove}
      />
      <mesh {...{ raycast: () => undefined }} renderOrder={2}>
        <bufferGeometry>
          <bufferAttribute
            attach="attributes-position"
            args={[selection.positions, 3]}
          />
        </bufferGeometry>
        <meshBasicMaterial
          color="#00d9ff"
          transparent
          opacity={0.04}
          side={DoubleSide}
          depthWrite={false}
          depthTest={!selection.cylinder}
          {...{
            toneMapped: false,
            polygonOffset: true,
            polygonOffsetFactor: -1,
            polygonOffsetUnits: -1,
          }}
          clippingPlanes={clippingPlanes}
        />
      </mesh>
      {selection.endCaps && (
        <mesh {...{ raycast: () => undefined }} renderOrder={3}>
          <bufferGeometry>
            <bufferAttribute
              attach="attributes-position"
              args={[selection.endCaps, 3]}
            />
          </bufferGeometry>
          <meshBasicMaterial
            color="#00d9ff"
            transparent
            opacity={0.2}
            side={DoubleSide}
            depthWrite={false}
            depthTest={false}
            {...{ toneMapped: false }}
            clippingPlanes={clippingPlanes}
          />
        </mesh>
      )}
      {[
        { color: "#102329", width: 6 },
        { color: "#00d9ff", width: 3 },
      ].map((stroke, index) => (
        <Line
          key={stroke.color}
          points={outlinePoints}
          segments
          color={stroke.color}
          lineWidth={stroke.width}
          renderOrder={3 + index}
          raycast={() => undefined}
          depthWrite={false}
          depthTest={!selection.cylinder}
          transparent={!!selection.cylinder}
          toneMapped={false}
          clippingPlanes={clippingPlanes}
        />
      ))}
      {ringPoints.length > 0 && (
        <Line
          points={ringPoints}
          segments
          color={CYLINDER_RING_STYLE.color}
          lineWidth={CYLINDER_RING_STYLE.width}
          opacity={CYLINDER_RING_STYLE.opacity}
          renderOrder={4}
          raycast={() => undefined}
          transparent
          depthWrite={false}
          depthTest={false}
          toneMapped={false}
          clippingPlanes={clippingPlanes}
        />
      )}
      {axisPoints.length === 2 &&
        [
          { color: "#102329", width: 5 },
          { color: "#ffffff", width: 2 },
        ].map((stroke, index) => (
          <Line
            key={`axis-${stroke.color}`}
            points={axisPoints}
            segments
            color={stroke.color}
            lineWidth={stroke.width}
            dashed
            dashSize={0.05}
            gapSize={0.025}
            renderOrder={5 + index}
            raycast={() => undefined}
            transparent
            depthWrite={false}
            depthTest={false}
            toneMapped={false}
            clippingPlanes={clippingPlanes}
          />
        ))}
    </group>
  );
}

/** @returns Surface measurements, computation status and reversible point isolation. */
export function SurfaceDimensionsCallout({
  selection,
  up,
  computing,
  isolated,
  onToggleIsolation,
  onActivate,
  onRemove,
}: {
  selection: WandSelection;
  up: Vector3;
  computing: boolean;
  isolated: boolean;
  onToggleIsolation(): void;
  onActivate?(): void;
  onRemove(): void;
}): JSX.Element | null {
  const unit = useDistanceUnit();
  const store = useSurfaceWandStore();
  const callout = useRef<HTMLDivElement>(null);
  const leader = useRef<HTMLDivElement>(null);
  const growing =
    computing || !selection.coverage || selection.coverage === "growing";
  const complete = !growing && selection.coverage === "complete";
  let statusLabel = "Partial";
  if (growing) statusLabel = "Computing";
  else if (complete) statusLabel = "Complete";
  if (isPipeRunSelection(selection)) {
    statusLabel = growing ? "Tracing" : "No stable continuation";
    if (selection.result?.ends.some((end) => end.reason === "branch")) {
      statusLabel = "Branch reached";
    } else if (
      selection.result?.ends.some((end) => end.reason === "incomplete")
    ) {
      statusLabel = "Incomplete coverage";
    } else if (
      selection.result?.ends.some((end) => end.reason === "resource-limit")
    ) {
      statusLabel = "Trace memory limit reached";
    }
  }
  let progressColor: "primary" | "success" | "warning" = "warning";
  if (growing) progressColor = "primary";
  else if (complete) progressColor = "success";
  const shape = wandSelectionShape(selection);
  const names = {
    plane: "plane",
    cylinder: "cylinder",
    "pipe-run": "pipe run",
  };
  const isolateLabel = `Show only selected ${names[shape]}`;
  const isolationLabel = isolated ? "Show all points" : isolateLabel;
  const title = {
    plane: "Plane",
    cylinder: "Cylinder",
    "pipe-run": "Pipe run",
  }[shape];
  const selectedTitle = `Selected ${names[shape]}`;
  const activationLabel = `Select ${names[shape]}`;
  function calculatePosition(
    object: Object3D,
    camera: Camera,
    size: { width: number; height: number },
  ): number[] {
    const element = callout.current;
    if (element) {
      element.style.maxWidth = `${Math.max(0, size.width - 2 * SURFACE_CALLOUT_MARGIN)}px`;
      element.style.maxHeight = `${Math.max(0, size.height - 2 * SURFACE_CALLOUT_MARGIN)}px`;
    }
    let positions: Float32Array;
    if (isPipeRunSelection(selection)) {
      positions = selection.centerline.length
        ? selection.centerline
        : new Float32Array([0, 0, 0]);
    } else {
      positions = selection.outline.length
        ? selection.outline
        : selection.positions;
    }
    const placement = positionSurfaceCallout(
      positions,
      object.parent?.matrixWorld ?? new Matrix4(),
      camera,
      size,
      {
        width: element?.offsetWidth ?? CALLOUT_WIDTH,
        height: element?.offsetHeight ?? CALLOUT_HEIGHT,
      },
    );
    if (element) element.style.visibility = placement ? "visible" : "hidden";
    const line = leader.current;
    if (line) {
      line.hidden = !placement;
      if (placement) {
        const direction = placement.anchor.clone().sub(placement.connector);
        line.style.left = `${placement.connector.x}px`;
        line.style.top = `${placement.connector.y}px`;
        line.style.width = `${direction.length()}px`;
        line.style.transform = `rotate(${Math.atan2(direction.y, direction.x)}rad)`;
      }
    }
    return placement ? placement.position.toArray() : [0, 0];
  }
  const dimensions = isPipeRunSelection(selection)
    ? {
        anchor: selection.mask.bounds.isEmpty()
          ? new Vector3()
          : selection.mask.bounds.getCenter(new Vector3()),
        height: selection.length,
        width: 0,
        area: 0,
      }
    : surfaceDimensions(
        selection.positions,
        new Vector3(selection.plane.x, selection.plane.y, selection.plane.z),
        up,
        selection.worldSpace ? selection : undefined,
      );
  if (!dimensions) return null;
  let values = [
    { label: "Width", value: formatDistance(dimensions.width, unit) },
    { label: "Height", value: formatDistance(dimensions.height, unit) },
    { label: "Surface area", value: formatArea(dimensions.area, unit) },
  ];
  if (isPipeRunSelection(selection)) {
    values = [
      { label: "Length", value: formatDistance(selection.length, unit) },
      ...(selection.inferredLength
        ? [
            {
              label: "Inferred",
              value: formatDistance(selection.inferredLength, unit),
            },
          ]
        : []),
    ];
  } else if (selection.cylinder) {
    values = [
      { label: "Length", value: formatDistance(dimensions.height, unit) },
      { label: "Diameter", value: formatDistance(dimensions.width, unit) },
    ];
  }
  return (
    <AppAwareHtml
      position={dimensions.anchor}
      calculatePosition={calculatePosition}
      zIndexRange={[0, 0]}
      style={{ pointerEvents: "none" }}
    >
      <Box
        ref={leader}
        aria-hidden
        sx={{
          position: "absolute",
          borderTop: "1px solid #00d9ff",
          transformOrigin: "0 0",
          pointerEvents: "none",
        }}
      />
      <Paper
        ref={callout}
        role="group"
        aria-label="Surface dimensions"
        elevation={0}
        onPointerDown={(event) => event.stopPropagation()}
        onPointerUp={(event) => event.stopPropagation()}
        onClick={(event) => event.stopPropagation()}
        onDoubleClick={(event) => event.stopPropagation()}
        onWheel={(event) => event.stopPropagation()}
        sx={{
          width: CALLOUT_WIDTH,
          maxWidth: "calc(100vw - 32px)",
          p: 1.5,
          bgcolor: grey[1000],
          color: grey[0],
          outline: `${grey[0]}33 solid 1px`,
          borderTop: "2px solid #00d9ff",
          position: "relative",
          overflow: "auto",
          pointerEvents: "auto",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 1,
            mb: 0.75,
          }}
        >
          <Typography
            sx={{ fontSize: 12, fontWeight: 600, flex: 1, minWidth: 0 }}
          >
            {onActivate ? title : selectedTitle}
          </Typography>
          {onActivate && (
            <Tooltip title={activationLabel}>
              <FaroIconButton
                aria-label={activationLabel}
                size="s"
                onClick={onActivate}
                sx={{ color: grey[0], width: 28, height: 28 }}
              >
                <AdsClickIcon fontSize="small" />
              </FaroIconButton>
            </Tooltip>
          )}
          {isPipeRunSelection(selection) && growing && store && (
            <Tooltip title="Stop tracing">
              <FaroIconButton
                aria-label="Stop tracing"
                size="s"
                onClick={() => closeSurfaceWand(store)}
              >
                <StopIcon fontSize="small" />
              </FaroIconButton>
            </Tooltip>
          )}
          <Tooltip title={isolationLabel}>
            <FaroIconButton
              size="s"
              aria-label={isolationLabel}
              aria-pressed={isolated}
              disabled={computing}
              onClick={onToggleIsolation}
              sx={{
                color: isolated ? "#00d9ff" : grey[0],
                width: 28,
                height: 28,
              }}
            >
              <FilterCenterFocusIcon fontSize="small" />
            </FaroIconButton>
          </Tooltip>
          <Tooltip title="Remove selection">
            <FaroIconButton
              size="s"
              aria-label="Remove selection"
              onClick={onRemove}
              sx={{ color: grey[0], width: 28, height: 28, flexShrink: 0 }}
            >
              <CloseIcon fontSize="small" />
            </FaroIconButton>
          </Tooltip>
        </Box>
        <Box
          component="dl"
          sx={{
            m: 0,
            display: "grid",
            gridTemplateColumns: "auto minmax(0, 1fr)",
            gap: "4px 12px",
            fontSize: 12,
          }}
        >
          {values.map(({ label, value }) => (
            <Box key={label} sx={{ display: "contents" }}>
              <Box component="dt" sx={{ color: grey[300] }}>
                {label}
              </Box>
              <Box
                component="dd"
                sx={{
                  m: 0,
                  textAlign: "right",
                  fontVariantNumeric: "tabular-nums",
                  overflowWrap: "anywhere",
                }}
              >
                <NoTranslate component="span">{`~${value}`}</NoTranslate>
              </Box>
            </Box>
          ))}
        </Box>
        {isAnalyticSelection(selection) && selection.cylinder && (
          <Box sx={{ mt: 1.25 }}>
            <Typography sx={{ fontSize: 11, mb: 0.5 }}>
              Radial deviation
            </Typography>
            <CylinderDeviationHistogram selection={selection} />
            <Box
              role="img"
              aria-label="Radial deviation scale"
              sx={{
                height: 5,
                background: `linear-gradient(90deg, ${CYLINDER_DEVIATION_COLORS.inside}, ${CYLINDER_DEVIATION_COLORS.fitted}, ${CYLINDER_DEVIATION_COLORS.outside})`,
              }}
            />
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                gap: 0.5,
                mt: 0.5,
                fontSize: 10,
                color: grey[300],
              }}
            >
              <Box sx={{ maxWidth: "45%", overflowWrap: "anywhere" }}>
                Inside
                <br />
                <NoTranslate component="span">{`-${formatDistance(selection.tolerance, unit)}`}</NoTranslate>
              </Box>
              <Box>0</Box>
              <Box
                sx={{
                  maxWidth: "45%",
                  overflowWrap: "anywhere",
                  textAlign: "right",
                }}
              >
                Outside
                <br />
                <NoTranslate component="span">{`+${formatDistance(selection.tolerance, unit)}`}</NoTranslate>
              </Box>
            </Box>
          </Box>
        )}
        {isPipeRunSelection(selection) && selection.result && (
          <Box sx={{ mt: 1, fontSize: 11, color: grey[300] }}>
            {selection.result.ends.map((end) => (
              <Typography key={end.direction} sx={{ fontSize: 11 }}>
                {`${end.direction === 1 ? "Forward" : "Backward"}: ${end.acceptedSteps === undefined ? "" : `${end.acceptedSteps} steps`}${end.length === undefined ? "" : `, ~${formatDistance(end.length, unit)}`} (${end.detail === "ambiguous-transition" ? "ambiguous geometry" : end.reason})`}
              </Typography>
            ))}
          </Box>
        )}
        <Box sx={{ mt: 1.25 }}>
          <Typography
            role="status"
            sx={{ fontSize: 12, color: grey[300], mb: 0.5 }}
          >
            {statusLabel}
          </Typography>
          <LinearProgress
            aria-label="Surface computation"
            aria-valuetext={statusLabel}
            variant={growing ? "indeterminate" : "determinate"}
            value={complete ? 100 : 0}
            color={progressColor}
            sx={{ height: 3 }}
          />
        </Box>
      </Paper>
    </AppAwareHtml>
  );
}

function CylinderDeviationHistogram({
  selection,
}: {
  selection: SurfaceSelection;
}): JSX.Element | null {
  const unit = useDistanceUnit();
  if (!selection.cylinder) return null;
  const bins = cylinderDeviationHistogram(
    selection.cylinderSamples,
    selection.cylinder,
    selection.tolerance,
  );
  const total = bins.reduce((sum, count) => sum + count, 0);
  const peak = Math.max(1, ...bins);
  const formatOptions = { useSmallUnits: true, useIncreasedMMPrecision: true };
  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          gap: 1,
          fontSize: 10,
          color: grey[300],
          mb: 0.5,
        }}
      >
        <Box>Sampled points</Box>
        <NoTranslate component="span">{total.toLocaleString()}</NoTranslate>
      </Box>
      <Box
        role="img"
        aria-label="Radial deviation histogram"
        aria-description={`${total} sampled points; largest bin ${total ? ((peak / total) * 100).toFixed(1) : 0}%`}
        sx={{
          height: 64,
          position: "relative",
          display: "grid",
          gridTemplateColumns: `repeat(${bins.length}, minmax(0, 1fr))`,
          gap: "1px",
          borderBottom: `1px solid ${grey[300]}`,
          mb: 0.5,
        }}
      >
        {bins.map((count, index) => {
          const fraction = index / (bins.length - 1);
          const color =
            fraction < 0.5
              ? new Color(CYLINDER_DEVIATION_COLORS.inside).lerp(
                  new Color(CYLINDER_DEVIATION_COLORS.fitted),
                  fraction * 2,
                )
              : new Color(CYLINDER_DEVIATION_COLORS.fitted).lerp(
                  new Color(CYLINDER_DEVIATION_COLORS.outside),
                  fraction * 2 - 1,
                );
          const lower = selection.tolerance * ((2 * index) / bins.length - 1);
          const upper =
            selection.tolerance * ((2 * (index + 1)) / bins.length - 1);
          return (
            <Tooltip
              key={index}
              title={`${formatDistance(lower, unit, formatOptions)} to ${formatDistance(upper, unit, formatOptions)}: ${count} ${count === 1 ? "sample" : "samples"} (${total ? ((count / total) * 100).toFixed(1) : 0}%)`}
            >
              <Box
                sx={{ height: "100%", display: "flex", alignItems: "flex-end" }}
              >
                <Box
                  sx={{
                    width: "100%",
                    height: `${(count / peak) * 100}%`,
                    bgcolor: color.getStyle(),
                  }}
                />
              </Box>
            </Tooltip>
          );
        })}
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            left: "50%",
            height: "100%",
            borderLeft: `1px dashed ${grey[300]}`,
            opacity: 0.5,
            pointerEvents: "none",
          }}
        />
        {!total && (
          <Typography
            sx={{
              position: "absolute",
              inset: 0,
              display: "grid",
              placeItems: "center",
              fontSize: 10,
              color: grey[300],
              pointerEvents: "none",
            }}
          >
            No samples available
          </Typography>
        )}
      </Box>
    </Box>
  );
}
